import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import { Link, useLocation } from 'react-router-dom';
import SeasonSelector from './SeasonSelector';
import { FaHeart, FaBookmark, FaRegHeart, FaRegBookmark } from 'react-icons/fa';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';

// Mobil breakpoint
const MOBILE_BREAKPOINT = 768;

const EpisodeContainer = styled.div`
  padding: ${props => props.$isMobile ? '20px 3% 30px 3%' : '40px 5% 50px 5%'};
  position: relative;
  z-index: 10;
  margin-top: ${props => props.$isMobile ? '20px' : '30px'};
  border-top: 1px solid var(--border-color);
`;

const Header = styled.div`
  display: flex;
  flex-direction: ${props => props.$isMobile ? 'column' : 'row'};
  justify-content: space-between;
  align-items: ${props => props.$isMobile ? 'stretch' : 'center'};
  margin-bottom: ${props => props.$isMobile ? '20px' : '25px'};
  gap: ${props => props.$isMobile ? '15px' : '0'};
`;

const Title = styled.h2`
  font-size: ${props => props.$isMobile ? '1.5rem' : '2rem'};
  font-weight: 700;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: ${props => props.$isMobile ? 'center' : 'left'};
`;

const MediaTypeBadge = styled.span`
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 10px;
  vertical-align: middle;
  
  ${props => {
    switch (props.type) {
      case 'anime':
        return `
          background: linear-gradient(135deg, #ff6b6b, #ee5a24);
          color: white;
        `;
      default:
        return `
          background: linear-gradient(135deg, #4ecdc4, #00b894);
          color: #111;
        `;
    }
  }}
`;

const SliderContainer = styled.div`
  position: relative;
  
  .swiper {
    overflow: visible;
    padding: ${props => props.$isMobile ? '10px 5px' : '20px 5px'};
  }

  .swiper-button-next,
  .swiper-button-prev {
    opacity: ${props => props.$isMobile ? '0.7' : '0'};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: ${props => props.$isMobile ? '35px' : '45px'};
    height: ${props => props.$isMobile ? '35px' : '45px'};
    background: radial-gradient(circle, rgba(10, 2, 28, 0.8) 50%, transparent 100%);
    border-radius: 50%;
    color: white;
    transition: all 0.3s ease;

    &::after {
      font-size: ${props => props.$isMobile ? '1rem' : '1.2rem'};
    }

    &:hover {
      background-color: var(--gradient-pink);
    }
  }
  
  .swiper-button-prev { left: ${props => props.$isMobile ? '5px' : '10px'}; }
  .swiper-button-next { right: ${props => props.$isMobile ? '5px' : '10px'}; }

  &:hover .swiper-button-next,
  &:hover .swiper-button-prev {
    opacity: 1;
  }

  .swiper-slide {
    width: auto;
  }
`;

const CardActions = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: ${props => props.$isMobile ? '1' : '0'};
  transition: opacity 0.3s ease;
  z-index: 5;
`;

const ActionButton = styled.button`
    background: rgba(0,0,0,0.6);
    border: none;
    color: white;
    width: ${props => props.$isMobile ? '28px' : '32px'};
    height: ${props => props.$isMobile ? '28px' : '32px'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: ${props => props.$isMobile ? '0.8rem' : '0.9rem'};
    transition: all 0.3s ease;

    &:hover {
        background: var(--gradient-pink);
        transform: scale(1.1);
    }
`;

const EpisodeCardWrapper = styled.div`
    position: relative;
    width: ${props => props.$isMobile ? '160px' : '200px'};
    
    &:hover ${CardActions} {
        opacity: 1;
    }
`;

const EpisodeCard = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;

  img {
    width: 100%;
    height: ${props => props.$isMobile ? '90px' : '112px'};
    object-fit: cover;
    display: block;
    filter: brightness(70%);
    transition: filter 0.3s ease;
  }

  .episode-info {
    padding: ${props => props.$isMobile ? '8px' : '10px'};
  }

  .episode-number {
    font-size: ${props => props.$isMobile ? '0.7rem' : '0.75rem'};
    color: var(--gradient-cyan);
    font-weight: 600;
    margin-bottom: 4px;
  }

  .episode-name {
    font-size: ${props => props.$isMobile ? '0.8rem' : '0.85rem'};
    font-weight: 600;
    color: var(--primary-color);
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 4px;
  }

  .episode-overview {
    font-size: ${props => props.$isMobile ? '0.65rem' : '0.7rem'};
    color: var(--secondary-color);
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  &:hover {
    border-color: var(--gradient-pink);
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(255, 0, 127, 0.3);
    
    img {
        filter: brightness(100%);
    }
  }
`;

// Optimized child component - parent state kullanır
const EpisodeCardComponent = ({ 
    episode, 
    selectedSeason, 
    isMobile, 
    isAnime, 
    tmdbId, 
    backdropPath, 
    posterPath, 
    user, 
    isWatchlisted, 
    onWatchlistToggle 
}) => {
    const [finalImageUrl, setFinalImageUrl] = useState(null);

    useEffect(() => {
        const imagePath = episode.thumbnail_path || backdropPath || posterPath;
        const potentialUrl = imagePath
            ? `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(imagePath)}&size=w300`
            : '/placeholder-image.jpg';

        if (potentialUrl === '/placeholder-image.jpg') {
            setFinalImageUrl(potentialUrl);
            return;
        }

        const img = new Image();
        img.src = potentialUrl;
        img.onload = () => setFinalImageUrl(potentialUrl);
        img.onerror = () => setFinalImageUrl('/placeholder-image.jpg');

    }, [episode.thumbnail_path, backdropPath, posterPath]);

    const handleWatchlistClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Parent callback'ini kullan
        onWatchlistToggle(episode.episode_number);
    };

    // DÜZELTİLMİŞ: tmdbId prop'unu kullan
    const watchUrl = `/watch/tv/${tmdbId}/season/${selectedSeason}/episode/${episode.episode_number}`;

    return (
        <Link to={watchUrl}>
            <EpisodeCardWrapper $isMobile={isMobile}>
                <EpisodeCard $isMobile={isMobile}>
                  {finalImageUrl ? (
                    <img 
                      src={finalImageUrl} 
                      alt={episode.episode_title || `Bölüm ${episode.episode_number}`}
                    />
                  ) : (
                    <div style={{
                      width: '100%', 
                      height: isMobile ? '90px' : '112px', 
                      backgroundColor: 'var(--glass-bg)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }} />
                  )}
                  <div className="episode-info">
                    <div className="episode-number">
                      {isAnime ? 'Bölüm' : 'Bölüm'} {episode.episode_number}
                    </div>
                    <div className="episode-name">
                      {episode.episode_title || `Bölüm ${episode.episode_number}`}
                    </div>
                    <div className="episode-overview">
                      {episode.episode_overview ? 
                        episode.episode_overview.substring(0, 40) + '...' : 
                        'Açıklama bulunmuyor.'
                      }
                    </div>
                  </div>
                </EpisodeCard>
                <CardActions $isMobile={isMobile}>
                    <ActionButton 
                        onClick={handleWatchlistClick} 
                        title={isWatchlisted ? "Watchlist'ten Kaldır" : "Sonra İzle"} 
                        $isMobile={isMobile}
                        style={{
                            background: isWatchlisted ? 'var(--gradient-pink)' : 'rgba(0,0,0,0.6)'
                        }}
                    >
                        {isWatchlisted ? <FaBookmark color="white" /> : <FaRegBookmark />}
                    </ActionButton>
                </CardActions>
            </EpisodeCardWrapper>
        </Link>
    );
};

const EpisodeSlider = ({ contentId, tmdbId, isMobile: propIsMobile, isAnime = false, backdropPath = null, posterPath = null }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = propIsMobile || windowWidth <= MOBILE_BREAKPOINT;
  const location = useLocation();

  // URL'den anime olup olmadığını kontrol et
  const isAnimeFromUrl = location.pathname.includes('/anime/');

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [user, setUser] = useState(null);
  const [watchlistStates, setWatchlistStates] = useState({}); // Tüm bölümler için watchlist durumu

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // User authentication check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setUser(data.user);
        }
      } catch (error) {
        console.error('Auth kontrol hatası:', error);
      }
    };
    checkAuth();
  }, []);

  // Bulk watchlist check - tüm bölümler için tek API çağrısı
  useEffect(() => {
    const checkBulkWatchlist = async () => {
      if (!user || !episodes.length || !contentId) return;

      try {
        // Tüm bölümler için paralel API çağrıları yerine tek çağrı yapalım
        const watchlistPromises = episodes.map(async (episode) => {
          const params = new URLSearchParams({
            content_id: contentId,
            season: selectedSeason,
            episode: episode.episode_number
          });
          
          try {
            const response = await fetch(`${getApiUrl(API_ENDPOINTS.CHECK_WATCHLIST)}?${params}`, {
              method: 'GET',
              credentials: 'include'
            });
            
            if (response.ok) {
              const data = await response.json();
              return {
                episodeId: episode.id,
                episodeNumber: episode.episode_number,
                isWatchlisted: data.isWatchlisted || false
              };
            }
          } catch (error) {
            console.error(`Bölüm ${episode.episode_number} watchlist kontrol hatası:`, error);
          }
          
          return {
            episodeId: episode.id,
            episodeNumber: episode.episode_number,
            isWatchlisted: false
          };
        });

        const results = await Promise.all(watchlistPromises);
        
        // State'i güncelle
        const newWatchlistStates = {};
        results.forEach(result => {
          newWatchlistStates[result.episodeNumber] = result.isWatchlisted;
        });
        
        setWatchlistStates(newWatchlistStates);
      } catch (error) {
        console.error('Bulk watchlist kontrol hatası:', error);
      }
    };

    checkBulkWatchlist();
  }, [user, episodes, contentId, selectedSeason]);

  // Sezonları çek
  useEffect(() => {
    const fetchSeasons = async () => {
      if (!contentId) return;
      
      try {
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${contentId}`);
        if (response.ok) {
          const seasonsData = await response.json();
          
          // Eğer API error döndürüyorsa
          if (seasonsData.error) {
            console.error('Sezonlar çekilirken hata:', seasonsData.error);
            setSeasons([]);
            return;
          }
          
          setSeasons(seasonsData);
          
          if (seasonsData.length > 0) {
            const firstSeason = seasonsData[0];
            setSelectedSeason(firstSeason.season_number);
            setSelectedSeasonId(firstSeason.id);
          }
        } else {
          console.error('Sezonlar çekilirken HTTP hatası:', response.status);
          setSeasons([]);
        }
      } catch (error) {
        console.error('Sezonlar çekilirken hata:', error);
        setSeasons([]);
      }
    };

    fetchSeasons();
  }, [contentId]);

  // Bölümleri çek
  useEffect(() => {
    const fetchEpisodes = async () => {
      if (!selectedSeasonId) return;
      
      setLoading(true);
      try {
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${selectedSeasonId}`);
        if (response.ok) {
          const episodesData = await response.json();
          
          // Eğer API error döndürüyorsa
          if (episodesData.error) {
            console.error('Bölümler çekilirken hata:', episodesData.error);
            setEpisodes([]);
            return;
          }
          
          setEpisodes(episodesData);
        } else {
          console.error('Bölümler çekilirken HTTP hatası:', response.status);
          setEpisodes([]);
        }
      } catch (error) {
        console.error('Bölümler çekilirken hata:', error);
        setEpisodes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEpisodes();
  }, [selectedSeasonId]);

  // Parent toggle fonksiyonu - anlık güncelleme için
  const handleWatchlistToggle = async (episodeNumber) => {
    if (!user) {
      alert('Watchlist\'e eklemek için giriş yapın!');
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.TOGGLE_WATCHLIST), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content_id: contentId,
          season_number: selectedSeason,
          episode_number: episodeNumber
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Parent state'i güncelle - anlık değişiklik için
          setWatchlistStates(prev => ({
            ...prev,
            [episodeNumber]: data.isInWatchlist
          }));
        }
      }
    } catch (error) {
      console.error('Watchlist toggle hatası:', error);
    }
  };

  // Sezon değiştiğinde season_id'yi güncelle
  const handleSeasonChange = (seasonNumber) => {
    setSelectedSeason(seasonNumber);
    const selectedSeasonData = seasons.find(s => s.season_number === seasonNumber);
    if (selectedSeasonData) {
      setSelectedSeasonId(selectedSeasonData.id);
    }
    // Sezon değiştiğinde watchlist state'ini temizle
    setWatchlistStates({});
  };

  // Swiper ayarlarını mobil/desktop'a göre optimize et
  const swiperConfig = {
    modules: [Navigation, FreeMode],
    spaceBetween: isMobile ? 12 : 15,
    slidesPerView: 'auto',
    navigation: true,
    freeMode: true,
    slidesOffsetBefore: isMobile ? 10 : 15,
    slidesOffsetAfter: isMobile ? 10 : 15,
  };

  // Final anime kontrolü - prop önceliklidir, yoksa URL'den kontrol edilir
  const finalIsAnime = isAnime || isAnimeFromUrl;

  if (!seasons || seasons.length === 0) {
    return null;
  }

  return (
    <EpisodeContainer $isMobile={isMobile}>
      <Header $isMobile={isMobile}>
        <Title $isMobile={isMobile}>
          Bölümler
        </Title>
        <SeasonSelector 
            seasons={seasons} 
            selectedSeason={selectedSeason} 
            onSeasonChange={handleSeasonChange}
        />
      </Header>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--secondary-color)' }}>
          {finalIsAnime ? 'Anime bölümleri yükleniyor...' : 'Bölümler yükleniyor...'}
        </div>
      ) : episodes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--secondary-color)' }}>
          {finalIsAnime ? 'Bu anime sezonu için henüz bölüm bulunmuyor.' : 'Bu sezon için henüz bölüm bulunmuyor.'}
        </div>
      ) : (
        <SliderContainer $isMobile={isMobile}>
          <Swiper {...swiperConfig}>
            {episodes.map(episode => (
              <SwiperSlide key={episode.id}>
                <EpisodeCardComponent 
                  episode={episode} 
                  selectedSeason={selectedSeason}
                  isMobile={isMobile}
                  isAnime={finalIsAnime}
                  tmdbId={tmdbId}
                  backdropPath={backdropPath}
                  posterPath={posterPath}
                  user={user}
                  isWatchlisted={watchlistStates[episode.episode_number] || false}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </SliderContainer>
      )}
    </EpisodeContainer>
  );
};

export default EpisodeSlider;
